'use client';

import React, { useEffect, useRef } from "react";
import { LanguageType, LanguageDirection, NavbarSectionContent, BrandInfoSectionContent } from "@/lib/types";
import { useLoading } from "@/contexts/LoadingContext";
import useAspectRatio from "@/hooks/useAspectRatio";
import NavbarContainer from "@/components/client/NavbarContainer";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { PageData } from "@/lib/types";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import { useIsLg } from '@/contexts/BreakpointContext';
import { setLanguage } from '@/lib/language';

interface NotFoundClientProps {
  pageData?: PageData | null;
  initialLanguage: LanguageType;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const NotFoundClient: React.FC<NotFoundClientProps> = ({ pageData, initialLanguage }) => {
  const language: LanguageType = initialLanguage;
  const isLg = useIsLg();
  const shadowRef = useRef<HTMLDivElement>(null);
  const sunRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const animatedNavigate = useAnimatedNavigate();
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  useEffect(() => {
    document.body.style.backgroundColor = '#FFF6E6';
  }, []);

  const navbarSectionPageData: NavbarSectionContent = {
    promoBarText: language === 'arabic' ? 'الصفحة غير موجودة' : 'Page not found',
    promoBarVisibility: true
  };
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData?.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;

  return (
    <div className="flex flex-col items-center relative h-screen bg-bg-light-primary overflow-hidden fixed inset-0">

      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        promoBarText={navbarSectionPageData?.promoBarText}
        promoBarVisibility={navbarSectionPageData?.promoBarVisibility || false}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
      />

      {/* Shadow overlay */}
      <div
        ref={shadowRef}
        className="absolute top-0 w-full aspect-[3/4] lg:aspect-[3/2] z-0 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      />

      {/* Main content */}
      <section className="
        w-full
        h-[100svh]
        flex items-center justify-center
        px-[1.25rem]
        lg:px-[3.5rem]
        py-[1.5rem]
        relative
      ">
        <div className="
          w-full
          h-full
          flex flex-col items-center justify-center
          pt-[3.5rem]
          pb-[1rem]
          lg:py-[3.5rem]
        ">

          {/* Content wrapper */}
          <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className="
              flex flex-col items-center justify-between
              gap-[2rem]
              lg:gap-[3.5rem]
              w-full
              h-full
            "
          >
            {/* Heading */}
            <div className="flex flex-col items-center justify-center gap-[1rem] lg:gap-[1.25rem] w-full flex-1">
              <h1 className="
                text-88-to-36--neue-june
                text-text-primary
                text-center
              ">
                {language === 'arabic' ? 'هل أضعت طريقك؟' : 'Lost Your Way?'}
              </h1>

              <p className="
                text-36-to-24--neue-june
                text-text-tertiary
                text-center
                max-w-[62.3125rem]
              ">
                {language === 'arabic' ? 'الصفحة غير موجودة، لكن إشراقتك لا تزال حاضرة. عُد للتصفح واستكشاف ملاذنا.' : 'Page missing, but your radiance isn\'t. Navigate back to explore our sanctuary.'}
              </p>
            </div>

            {/* Bottom section: Sun and social buttons */}
            <div className="flex flex-col w-full max-w-[62.3125rem] flex-none">
              {/* Sun with separator line */}
              <div className="relative w-full pointer-events-none">
                <div ref={lineRef} className="h-[1.5px] bg-stroke-soft-200 relative z-10 w-full"></div>

                <div className="
                  absolute
                  top-[-21.875rem]
                  lg:top-[-21rem]
                  left-1/2
                  -translate-x-1/2
                  max-w-full
                  w-[25rem]
                  h-[21.875rem]
                  lg:w-[27.25rem]
                  lg:h-[21rem]
                  overflow-hidden
                  flex items-center justify-center
                  z-10
                ">
                  <div
                    ref={sunRef}
                    className='
                      w-[6.5rem]
                      h-[6.5rem]
                      lg:w-[9rem]
                      lg:h-[9rem]
                      rounded-full
                      bg-theme-yellow-primary-base
                      shadow-sunshine-wide-glow
                      absolute
                      bottom-[-3.25rem]
                      lg:bottom-[-4.5rem]
                    '
                  />
                </div>
              </div>

              {/* Bottom text and social buttons */}
              <div className="
                flex flex-col items-center justify-center
                gap-[2rem]
                lg:gap-[2.5rem]
                w-full
                pt-[1.5rem]
                lg:pt-[2rem]
              ">
              <p className="
                text-18--alexandria
                text-text-quaternary
                text-center
              ">
                {language === 'arabic' ? 'عد الى صفحتنا الرئيسية أو تواصل مع أحد مختصينا لإرشادك الى الوجهة الصحيحة' : 'Return to our main page or contact our specialists to guide you to the right place.'}
              </p>

              {/* Social buttons */}
              <div className="flex flex-row flex-wrap gap-[1rem] justify-center items-center">
                <CompactButton
                  size={isLg ? 56 : 44}
                  radius={9999}
                  radialFill={true}
                  onClick={() => window.open(brandInfoSectionPageData?.instagramLink, '_blank')}
                >
                  <img src="/Brand-Instagram.svg" alt="Instagram icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton
                  size={isLg ? 56 : 44}
                  radius={9999}
                  radialFill={true}
                  onClick={() => window.open(brandInfoSectionPageData?.tiktokLink, '_blank')}
                >
                  <img src="/Brand-Tiktok.svg" alt="Tiktok icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton
                  size={isLg ? 56 : 44}
                  radius={9999}
                  radialFill={true}
                  onClick={() => window.open(brandInfoSectionPageData?.facebookLink, '_blank')}
                >
                  <img src="/Brand-Facebook.svg" alt="Facebook icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton
                  size={isLg ? 56 : 44}
                  radius={9999}
                  radialFill={true}
                  onClick={() => window.open(brandInfoSectionPageData?.linkedinLink, '_blank')}
                >
                  <img src="/Brand-Linkedin.svg" alt="Linkedin icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton
                  size={isLg ? 56 : 44}
                  radius={9999}
                  radialFill={true}
                  onClick={() => window.open(brandInfoSectionPageData?.snapchatLink, '_blank')}
                >
                  <img src="/Brand-Snapchat.svg" alt="Snapchat icon" className="w-full z-10" />
                </CompactButton>
              </div>
            </div>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
}

export default NotFoundClient;
